import { AxiosRequestConfig } from "axios";

import { PortfolioHoldingsType } from "@/app/dashboard/types";

import { axiosInstance } from "../api/axiosConfig";
import { addAnalyticsData, addHoldingsData, addSnapshotsData, isDexieEnabled } from "../db";
import { HoldingsQueries } from "../db/quries";
import { setFocusedPortfolio, setPortfolios } from "../redux/dashboard.slice";
import { store } from "../redux/store";

export const getDashboardAnalytics = async (options: AxiosRequestConfig) => {
  const data = await axiosInstance.get(`/trade-engine/analytics`, options)
  if ([200, "200", 201, "201"].includes(data.status)) {
    addAnalyticsData(data.data.data)
  }
  return data.data.data;
};

export const getDashboardHoldings = async (options: AxiosRequestConfig) => {
  if (isDexieEnabled())
    HoldingsQueries.getHoldings(list => {
      if (list != null && list.length) {
        store.dispatch(setPortfolios(list as any));
        // Always resolve a focused portfolio with a safe fallback chain:
        // exact match on the selected exchange -> aggregate ("All", id 0) ->
        // first available. Previously this only ran when list.length > 1 and
        // had no fallback, so a missing match left focusedPortfolio undefined
        // and every value rendered as 0.
        const targetId = options.params["exchange_key_id"] ?? 0;
        const focused =
          list.find((e) => e.id === targetId) ??
          list.find((e) => e.id === 0) ??
          list[0];
        if (focused) store.dispatch(setFocusedPortfolio(focused as any));
      }
    });

  await axiosInstance
    .get("/trade-engine/portfolio-holdings", options)
    .then(async ({ data }) => {
      await addHoldingsData(data.data)
      if (!isDexieEnabled()) {
        // Dexie is off: dispatch straight to Redux, but apply the SAME transform
        // the cache write path (addHoldingsData) applied, so the UI receives
        // identical data. Both the Providers table and the focused-portfolio
        // lookup match on `id`, and the aggregate ("All") portfolio has no
        // exchange_key_id, so normalize each row's key to `exchange_key_id ?? 0`.
        // Without this, id matches miss and every value renders as 0 / "No results".
        const normalizeId = (e: PortfolioHoldingsType) => ({
          ...e,
          id: (e as unknown as { exchange_key_id?: number }).exchange_key_id ?? 0,
        });
        if (Array.isArray(data.data)) {
          const list = (data.data as PortfolioHoldingsType[]).map(normalizeId);
          store.dispatch(setPortfolios(list));
          const targetId = options.params["exchange_key_id"] ?? 0;
          const focused =
            list.find((e) => e.id === targetId) ??
            list.find((e) => e.id === 0) ??
            list[0];
          if (focused) store.dispatch(setFocusedPortfolio(focused));
        } else if (data.data) {
          store.dispatch(setFocusedPortfolio(normalizeId(data.data)));
        }
      }
    });
};

export const getDashboardSnapshots = async (options: AxiosRequestConfig) => {
  const data = await axiosInstance.get("/trade-engine/portfolio-snapshots", options);
  addSnapshotsData(data.data.data, options.params.snapshot_range, options.params.exchange_key_id)
  return data.data;
};

export const getSummary = async (
  options: AxiosRequestConfig,
  type?: "SA" | "Market",
) => {
  if (type === "SA")
    return await axiosInstance.get("/ai/smart-allocation-summary", options);
  else if (type === "Market")
    return await axiosInstance.get("/ai/market-summary", options);
  return await axiosInstance.get("/ai/portfolio-summary", options);
};


export const createDemo = async (options?: AxiosRequestConfig) => {
  return await axiosInstance.get('/exchange/demo-account/create', options)
}